import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as OrganizationsService from '../services/OrganizationsService';
import { Organization } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Page size is a SERVER concern under token pagination — fixed by the module and
// sent as `limit`, never chosen by the user. There is no rows-per-page control.
const DEFAULT_LIMIT = 10;

// Query keys
export const organizationsKeys = {
    all: ['organizations'] as const,
    lists: () => [...organizationsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...organizationsKeys.lists(), { filters }] as const,
    details: () => [...organizationsKeys.all, 'detail'] as const,
    detail: (id: string) => [...organizationsKeys.details(), id] as const,
};

type OrganizationsPage = {
    items: Organization[];
    nextToken?: string | null;
};

// Hooks
// FNP-563: token pagination. `organizationsKeys.list(filters)` puts EVERY filter
// in the query key — omit one and changing the filter silently serves the
// previous filter's cached pages.
//
// Exported as options rather than inlined so `getNextPageParam` can be asserted
// directly. React Query accepts a `null` return as "no more pages", so no
// behavioural test can tell the correct `undefined` from the nullish form that
// causes an infinite refetch — only a direct call on this function can.
export const organizationsQueryOptions = (filters: Record<string, string> = {}) => ({
    queryKey: organizationsKeys.list(filters),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
        OrganizationsService.fetchRecords({
            filter: filters,
            limit: DEFAULT_LIMIT,
            nextToken: pageParam,
        }),
    // MUST be `undefined`, never `null` and never the same token again:
    // either of those makes React Query refetch forever.
    getNextPageParam: (lastPage: OrganizationsPage | undefined): string | undefined => {
        if (!lastPage || !lastPage.nextToken) return undefined;
        return lastPage.nextToken;
    },
    initialPageParam: null as string | null,
});

export const useOrganizationsQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery(organizationsQueryOptions(filters));
};

export const useOrganizationQuery = (id: string) => {
    return useQuery({
        queryKey: organizationsKeys.detail(id),
        queryFn: () => OrganizationsService.fetch(id),
        enabled: !!id,
    });
};

export const useCreateOrganizationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: OrganizationsService.add,
        onSuccess: () => {
            notifySuccess('Organization created');
            queryClient.invalidateQueries({ queryKey: organizationsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateOrganizationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: Organization }) =>
            OrganizationsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Organization updated');
            queryClient.invalidateQueries({ queryKey: organizationsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: organizationsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeleteOrganizationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: OrganizationsService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Organization deleted');
            queryClient.invalidateQueries({ queryKey: organizationsKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useOrganizationsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
